import React from "react";
import { Tabs } from "antd";
import { ShopOutlined, UserOutlined } from "@ant-design/icons";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useIsMobile from "../hooks/useIsMobile";

const { TabPane } = Tabs;

const ENTITY_TABS = [
  {
    key: "clients",
    label: "Clients",
    icon: <UserOutlined />,
    path: "/gestion-entites/clients",
    permission: "Gestion Commerciale.Entites.Clients.view",
  },
  {
    key: "fournisseurs",
    label: "Fournisseurs",
    icon: <ShopOutlined />,
    path: "/gestion-entites/fournisseurs",
    permission: "Gestion Commerciale.Entites.Fournisseurs.view",
  },
];

const getAccessibleEntityTabs = (hasPermission) =>
  ENTITY_TABS.filter((tab) => hasPermission(tab.permission));

const getDefaultEntityTabPath = (hasPermission) =>
  getAccessibleEntityTabs(hasPermission)[0]?.path || "/accueil";

export const EntityTabsRedirect = () => {
  const { hasPermission } = useAuth();

  return <Navigate to={getDefaultEntityTabPath(hasPermission)} replace />;
};

const EntityTabsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isMobile = useIsMobile();

  const availableTabs = React.useMemo(
    () => getAccessibleEntityTabs(hasPermission),
    [hasPermission]
  );

  const activeTab = React.useMemo(() => {
    return (
      availableTabs.find((tab) => location.pathname.startsWith(tab.path))?.key ||
      availableTabs[0]?.key
    );
  }, [availableTabs, location.pathname]);

  const handleTabChange = (key) => {
    const targetTab = availableTabs.find((tab) => tab.key === key);

    if (targetTab && targetTab.path !== location.pathname) {
      navigate(targetTab.path);
    }
  };

  if (!availableTabs.length || !activeTab) {
    return <Outlet />;
  }

  // Sur mobile, les deux onglets sont rendus par EntitesMobile, un écran .epm déjà haut
  // de 100dvh − tabbar : la barre d'onglets AntD le pousserait hors du viewport. Il
  // fournit sa propre navigation (feuille « Aller à… »).
  if (isMobile) {
    return <Outlet />;
  }

  return (
    <Tabs
      activeKey={activeTab}
      onChange={handleTabChange}
      size="large"
      type="card"
      destroyInactiveTabPane
    >
      {availableTabs.map((tab) => (
        <TabPane
          tab={
            <span>
              {tab.icon} {tab.label}
            </span>
          }
          key={tab.key}
        >
          <Outlet />
        </TabPane>
      ))}
    </Tabs>
  );
};

export default EntityTabsLayout;
